import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Popover } from 'antd';

// API
import { createNode, createTextNode } from '../../../api/sequence';
import { validateURL } from '../../../../../helper/checkUrls';

// Assets
import IconContainer from 'assets/customSVG/sequence/actions/IconContainer';
import AddIcon from '../../../../../assets/customSVG/AddIcon';
import Tick from '../../../../../assets/customSVG/Tick';
import Xmark from '../../../../../assets/customSVG/Xmark';
import { ICONS } from '../../../../../assets/icons';

// Constants
import {
  ALLOWED_BODY_NODE_TYPES,
  ALLOWED_BUTTONS_NODE_TYPES,
  ALLOWED_FOOTER_NODE_TYPES,
  ALLOWED_HEADER_NODE_TYPES,
  AUDIO_TYPE,
  DEFAULT_MEDIA_SIZE,
  DOCUMENT_TYPE,
  FOOTER_TYPES,
  IMAGE_TYPE,
  maxButtonCount,
  NONE_TYPE,
  TEXT_TYPE,
  VIDEO_TYPE,
} from '../../../constants/sequence';
import {
  ACTIONS_LIST,
  OPERATIONS_LIST,
  NODE_TYPES_CONVERSION,
} from '../../../constants/triggers';

// Helpers
import { cn } from 'helper/cn';
import { isObjectEmpty } from '../../../helpers';
import {
  isValidBody,
  isValidButton,
  isValidCtaUrl,
  isValidFlow,
  isValidDefaultReply,
  isValidFooter,
  isValidHeader,
  isValidList,
  isValidContact,
  isValidTemplate,
  isValidAssignee,
} from '../../../helpers/sequence/node';

// Redux
import { updateToggleToast } from '../../../../../reduxToolkit/appSlice';
import {
  useAspDispatch,
  useAspSelector,
} from '../../../../../test/jest-redux-hooks';

// Hooks
import useToggle from '../../../../../hooks/useToggle';

// Components
import Loader from '../../../../../components/commonComponents/Loader/Index';
import Input from '../Input';
import Tabs from '../Tabs';
import BodyMessage from './BodyMessage';
import DefaultReplay from './DefaultReplay';
import HeaderMessage from './HeaderMessage';
import ListMessage from './list';
import QuickReplyButtons from './QuickReplyButtons';
import TemplateMessage from './TemplateMessage';
import ContactMessage from './ContactMessage';
import FollowUps from './operations/FollowUps';
import Assignee from './operations/assignee';
import FlowMessage from './FlowMessage';

// Notification
import NotificationContext from '../../../../../providers/NotificationProvider';
import { 
  showSuccessToast, 
  showErrorToast 
} from '../../../../../components/commonComponents/gxAntdToast/toastUtils';

const EditMessages = ({
  originalData,
  node,
  onTriggerClick,
  onChange,
  findEmptySpace,
  toggleNodeEditMode,
  allowToEditFlow,
  removeEdges,
  updateEdgesCoordinates,
  handleCancelEditMode,
  getUnSavedEdge,
  findPreviousNode
}) => {
  // Hooks
  const { sequence_id, campaign_id } = useParams();
  const { currentBrand, toggleToast } = useAspSelector((state) => state.app);
  const dispatch = useAspDispatch();
  const notification = useContext(NotificationContext);
  const mediaRef = useRef(null);

  // State
  const [isUpdating, setIsUpdating] = useToggle(false);
  const [isHeaderTypeChange, setIsHeaderTypeChange] = useToggle(false);
  const [headerList, setHeaderList] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [removedEdges, setRemovedEdges] = useState([]);
  const [footerTab, setFooterTab] = useState(FOOTER_TYPES?.[0]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [error, setError] = useToggle(false);
  // Effects
  useEffect(() => {
    initializeFooterTab();
    initializeSelectedTemplate();
  }, [node]);


  // Helper Functions
  const initializeFooterTab = () => {
    if (
      ['BUTTON', 'LIST', 'FLOW', 'CTA_URL']?.includes(node?.type) &&
      node?.data?.interactive?.footer?.text
    ) {
      const footer = node?.data?.interactive?.footer;
      const keys = Object?.keys(footer);
      const current = FOOTER_TYPES?.filter(
        (f) => f?.key?.toLowerCase() === keys?.[0]
      );
      setFooterTab(current?.[0] || FOOTER_TYPES?.[0]);
    }
  };

  const initializeSelectedTemplate = () => {
    if (['TEMPLATE']?.includes(node?.type) && !selectedTemplate) {
      const datas = {
        ...node?.data,
        name: node?.data?.template_name,
      };
      const value = datas?.template?.length > 0 ? datas : null;
      setSelectedTemplate(value);
    }
  };

  const getHeaderTypes = useCallback(() => {
    const HEADER_TYPES = ['BUTTON', 'FLOW', 'CTA_URL']?.includes(node?.type)
      ? [NONE_TYPE, TEXT_TYPE, IMAGE_TYPE, VIDEO_TYPE, DOCUMENT_TYPE]
      : ['LIST']?.includes(node?.type)
      ? [NONE_TYPE, TEXT_TYPE]
      : [IMAGE_TYPE, VIDEO_TYPE, AUDIO_TYPE, DOCUMENT_TYPE];

    const types = ['BUTTON', 'LIST', 'FLOW', 'CTA_URL']?.includes(node?.type)
      ? node?.data?.interactive?.header?.type
      : node?.data?.type;

    const selected = HEADER_TYPES?.filter(
      (h) => h?.key === types?.toUpperCase()
    );

    return { selected, list: HEADER_TYPES };
  }, [node]);

  useEffect(() => {
    const { list } = getHeaderTypes();
    setHeaderList(list);
  }, [getHeaderTypes]);

  useEffect(() => {
    const { selected, list } = getHeaderTypes();
    setSelectedTab(selected?.length === 0 ? list?.[0] : selected?.[0]);
  }, [getHeaderTypes]);

  const handleToastError = useCallback((message) => {
    dispatch(
      updateToggleToast([
        ...toggleToast,
        {
          id: toggleToast?.length + 1,
          content: message,
          status: 'Error',
          duration: '',
        },
      ])
    );
  }, [dispatch, toggleToast]);

  const showSubmit = useCallback(() => {
    if (error) return false;
    
    const validations = [
      isValidHeader({ node, selectedTab }),
      isValidBody({ node }),
      isValidFooter({ node, footerTab }),
      isValidTemplate({ node, selectedTemplate }),
      isValidDefaultReply({ node }),
      isValidContact({ node }),
      isValidAssignee({ node }),
    ];

    // Type-specific validations
    if (node?.type === 'BUTTON') {
      validations.push(isValidButton({ node }));
    } else if (node?.type === 'LIST') {
      validations.push(isValidList({ node }));
    } else if (node?.type === 'FLOW') {
      validations.push(isValidFlow({ node }));
    } else if (node?.type === 'CTA_URL') {
      validations.push(isValidCtaUrl({ node }));
    }

    return validations.every(Boolean);
  }, [error, node, selectedTab, footerTab, selectedTemplate]);

  const formatDurationLabel = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(seconds / 3600);
    const days = Math.floor(seconds / 86400);

    if (days > 0) {
      return `Wait for ${days} day${days === 1 ? '' : 's'}`;
    } else if (hours > 0) {
      return `Wait for ${hours} hour${hours === 1 ? '' : 's'}`;
    } else {
      return `Wait for ${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
  }, []);

  const handleLabel = useCallback(() => {
    if (node.type?.toLowerCase() === 'follow_up') {
      return formatDurationLabel(node.data.operations.delay);
    }
    return node.label || 'Whatsapp';
  }, [node, formatDurationLabel]);

  // Event Handlers
  const handleBodyChange = useCallback((value) => {
    let payload = {};

    if (['BUTTON', 'LIST', 'LOCATION_REQUEST_MESSAGE', 'FLOW', 'CTA_URL']?.includes(node?.type)) {
      payload = {
        primaryKey: 'data',
        secondaryKey: 'interactive',
        key: 'body',
        data: { text: value },
      };
    } else {
      payload = {
        key: 'data',
        data: {
          type: node?.type?.toLowerCase() === 'payment_captured' || node?.type?.toLowerCase() === 'payment_failed' ? 'text' : node?.type?.toLowerCase(),
          [`${node?.type?.toLowerCase() === 'payment_captured' || node?.type?.toLowerCase() === 'payment_failed' ? 'text' : node?.type?.toLowerCase()}`]: { body: value },
        },
      };
    }

    onChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      ...payload,
    });
  }, [node?.type, node?.uniq_id, onChange]);

  const handleFooterChanges = useCallback((value) => {
    const payload = {
      primaryKey: 'data',
      secondaryKey: 'interactive',
      key: 'footer',
      data: { text: value },
    };

    onChange({
      type: 'UPDATE_NODE',
      id: node?.uniq_id,
      ...payload,
    });
  }, [node?.uniq_id, onChange]);

  const handleFooterTabChange = useCallback((data) => {
    if (allowToEditFlow) {
      setFooterTab(data);
      const interactive = { ...node?.data?.interactive };
      
      if (data?.key === 'NONE' && Object?.keys(interactive)?.includes('footer')) {
        delete interactive['footer'];
        onChange({
          type: 'UPDATE_NODE',
          id: node?.uniq_id,
          primaryKey: 'data',
          key: 'interactive',
          data: interactive,
        });
      }
    }
  }, [allowToEditFlow, node?.data?.interactive, node?.uniq_id, onChange]);

  // Business Logic
  const buildPayloadData = useCallback(() => {
    const payloadData = {};
    const isSameNode = originalData?.uniq_id === node?.uniq_id;
    const isSameType = originalData?.type === node?.type;

    if (node?.settings?.other_replies) {
      payloadData['other_replies'] = node?.settings?.other_replies;
    } else if (isObjectEmpty(node?.settings?.other_replies)) {
      payloadData['other_replies'] = {};
    }

    if (node?.type === 'TEMPLATE') {
      const {
        bodyVariables,
        headerVariable,
        message_template_id,
        subcategory_type,
        ...rest
      } = selectedTemplate;

      const { id, language, category, name, payment_details, ...urls } = rest;

      let variables = [...new Set([...bodyVariables, ...headerVariable])];
      variables = variables?.filter((v) => v !== null);
      variables = Object.fromEntries(
        variables.map((item) => [
          item?.title?.replace(/{{|}}/g, ''),
          item?.value,
        ])
      );

      payloadData['message_body'] = {
        type: selectedTemplate?.category,
        subcategory_type: selectedTemplate?.subcategory_type,
        template_name: selectedTemplate?.name,
        variables: [{ ...variables, ...urls }],
        template_id: selectedTemplate?.message_template_id,
        ...(selectedTemplate?.payment_details?.products?.length > 0 && {
          payment_details: {
            products: selectedTemplate.payment_details.products || [],
            order_details: selectedTemplate.payment_details.order_details || {}
          }
        })
      };

      removeEdges(node?.uniq_id);
    } else {
      const data = { ...node?.data };
      if (data?.interactive?.header?.type === 'none') {
        delete data?.['interactive']?.['header'];
      }
      payloadData['message_body'] = data;
    }

    return { payloadData, isSameNode, isSameType };
  }, [originalData, node, selectedTemplate, removeEdges]);

  const calculateHeight = useCallback((originalSize, updatedSize) => {
    return originalSize === updatedSize
      ? 0
      : originalSize < updatedSize
      ? updatedSize - originalSize - 10
      : updatedSize - originalSize + 10;
  }, []);

  const submitNewNode = useCallback(async ({ payloadData }) => {
    const settings = {
      ...node?.settings,
      defaults: { ...node?.settings?.defaults, isEditMode: false },
    };

    const payload = {
      brand_id: currentBrand?.brand_id,
      campaign_id: campaign_id,
      node_type: node?.type,
      label: handleLabel(),
      coordinates: findEmptySpace(),
      save: true,
      message_body: payloadData?.message_body,
      settings,
    };

    const edge = await getUnSavedEdge(node?.uniq_id);

    if (edge) {
      payload['edges'] = {
        starting_node: {
          connection_point: edge?.connection_point?.id,
          node_id: edge?.start_id,
        },
      };
      payload['edge_coordinates'] = { ...edge?.coordinates };
      payload['coordinates'] = node?.coordinates;
    }

    if (payloadData?.other_replies) {
      payload['other_replies'] = payloadData?.other_replies;
    }

    try {
      const newNode = await createNode(sequence_id, payload);

      if (newNode?.status === 200) {
        onChange({
          type: 'ADD_NODE',
          data: {
            ...newNode?.data?.data,
            data: !newNode?.data?.data?.data
              ? {
                  type: ['BUTTON', 'LIST']?.includes(newNode?.data?.data?.type)
                    ? 'interactive'
                    : newNode?.data?.data?.type?.toLowerCase(),
                }
              : { ...newNode?.data?.data?.data },
          },
        });

        if (newNode?.data?.edge) {
          onChange({
            type: 'REPLACE_EDGE',
            id: edge?.uniq_id,
            data: newNode?.data?.edge,
          });
        }

        onChange({ type: 'DELETE_NODE', id: node?.uniq_id });
        onChange({ type: 'UPDATE_DATA', data: { selectedNode: null } });

        if (originalData?.uniq_id === node?.uniq_id && originalData?.type !== node?.type) {
          removeEdges(node?.uniq_id);
        }
        
        setIsUpdating(false);
      } else {
        const errorMessage = newNode?.response?.data?.message || 
                           newNode?.response?.data?.error || 
                           'Failed to create NODE';
        showErrorToast(notification, errorMessage);
        setIsUpdating(false);
      }
    } catch (error) {
      setIsUpdating(false);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          'Failed to create NODE';
      showErrorToast(notification, errorMessage);
    }
  }, [node, originalData, currentBrand, campaign_id, sequence_id, handleLabel, 
      findEmptySpace, getUnSavedEdge, onChange, removeEdges, notification]);

  const updateExistingNode = useCallback(async ({ payloadData, isSameNode, isSameType }) => {
    let updatedEdgesData = null;
    const originalNodeMedia = originalData?.data?.interactive?.header;
    const updatedNodeMedia = node?.data?.interactive?.header;

    const isOriginalNodeHasLink = originalNodeMedia && originalNodeMedia?.[originalNodeMedia?.type]?.link !== '';
    const isUpdatedNodeHasLink = updatedNodeMedia && updatedNodeMedia?.[updatedNodeMedia?.type]?.link !== '';
    const isSameHeaderType = originalNodeMedia?.type === updatedNodeMedia?.type;

    if (isSameNode && isSameType) {
      const originalHeaderSize = DEFAULT_MEDIA_SIZE[originalNodeMedia?.type];
      const updatedHeaderSize = DEFAULT_MEDIA_SIZE[updatedNodeMedia?.type];

      const isOriginalNodeHasFooter = originalData?.data?.interactive?.footer?.text?.length > 0;
      const isNewNodeHasFooter = node?.data?.interactive?.footer?.text?.length > 0;

      let isFooterHeight = 0;
      if (!isOriginalNodeHasFooter && isNewNodeHasFooter) isFooterHeight = 27;
      if (isOriginalNodeHasFooter && !isNewNodeHasFooter) isFooterHeight = -26;

      if (isOriginalNodeHasLink && isUpdatedNodeHasLink && !isSameHeaderType) {
        const height = calculateHeight(originalHeaderSize, updatedHeaderSize);
        updatedEdgesData = updateEdgesCoordinates({
          startNodeId: originalData?.uniq_id,
          removedEdges: removedEdges,
          data: { height: height + isFooterHeight },
        });
      } else if (isOriginalNodeHasLink && !isUpdatedNodeHasLink) {
        const height = ['text', 'audio']?.includes(originalNodeMedia?.type)
          ? originalHeaderSize + 20
          : originalHeaderSize + 10;

        updatedEdgesData = updateEdgesCoordinates({
          startNodeId: originalData?.uniq_id,
          removedEdges: removedEdges,
          data: { height: -height + isFooterHeight },
        });
      } else if (!isOriginalNodeHasLink && isUpdatedNodeHasLink) {
        const height = ['text', 'audio']?.includes(updatedNodeMedia?.type)
          ? updatedHeaderSize + 20
          : updatedHeaderSize + 10;

        updatedEdgesData = updateEdgesCoordinates({
          startNodeId: originalData?.uniq_id,
          removedEdges: removedEdges,
          data: { height: height + isFooterHeight },
        });
      } else if (removedEdges?.length > 0 || isFooterHeight !== 0) {
        updatedEdgesData = updateEdgesCoordinates({
          startNodeId: originalData?.uniq_id,
          removedEdges: removedEdges,
          data: { height: 0 + isFooterHeight },
        });
      }
    }

    if (showSubmit() && allowToEditFlow) {
      let payload = {
        brand_id: currentBrand?.brand_id,
        campaign_id: campaign_id,
        node_type: node?.type,
        label: handleLabel(),
        save: true,
        message_body: {},
        ...payloadData,
      };

      if (updatedEdgesData?.currentNodeUpdatedEdges?.length > 0) {
        payload.edges = updatedEdgesData.currentNodeUpdatedEdges;
      }

      if (removeEdges?.length > 0) {
        payload.deleted_edges = removedEdges;
      }

      try {
        const sendNode = await createTextNode(sequence_id, node?.uniq_id, payload);

        if (sendNode?.status === 200) {
          onChange({
            type: 'UPDATE_NODE_DATA',
            id: node?.uniq_id,
            data: sendNode?.data?.data,
          });

          if (removeEdges?.length > 0) setRemovedEdges([]);

          const stateData = { selectedNode: null };

          if (isSameNode && isSameType) {
            const edgeData = updatedEdgesData?.edges;
            if (edgeData) stateData.edges = edgeData;
          } else {
            removeEdges(node?.uniq_id);
          }

          onChange({ type: 'UPDATE_DATA', data: stateData });
          setIsUpdating(false);
        } else {
          setIsUpdating(false);
          handleToastError(sendNode?.data?.message);
        }
      } catch (error) {
        setIsUpdating(false);
        handleToastError('Failed to update node');
      }
    }
  }, [node, showSubmit, originalData, removedEdges, removeEdges, setRemovedEdges, 
      calculateHeight, updateEdgesCoordinates, allowToEditFlow, currentBrand, 
      campaign_id, handleLabel, sequence_id, onChange, handleToastError]);

  const allowToUpdateTemplateNode = useCallback(() => {
    return node?.status !== 'UN_SAVED'
      ? ['TEMPLATE']?.includes(node?.type) && selectedTemplate
        ? node?.data?.template_name === selectedTemplate?.name
        : false
      : selectedTemplate?.name;
  }, [node, selectedTemplate]);

  const handleOk = useCallback(async () => {
    setIsUpdating(true);
    const { payloadData, isSameNode, isSameType } = buildPayloadData();

    try {
      if (showSubmit() && node?.status === 'UN_SAVED') {
        await submitNewNode({ payloadData });
      } else {
        if (allowToUpdateTemplateNode()) {
          if (toggleNodeEditMode) {
            toggleNodeEditMode({
              id: node?.id,
              uniqId: node?.uniq_id,
              value: 'false',
              defaults: node?.settings?.defaults,
            });
          }
          return;
        }
        await updateExistingNode({ payloadData, isSameNode, isSameType });
      }
    } catch (error) {
      console.warn(error);
      setIsUpdating(false);
    }
  }, [buildPayloadData, showSubmit, node, submitNewNode, allowToUpdateTemplateNode, 
      toggleNodeEditMode, updateExistingNode]);

  // Utility Functions
  const allowToAddButton = useCallback(() => {
    return node?.data?.interactive?.action?.buttons?.length < maxButtonCount;
  }, [node?.data?.interactive?.action?.buttons?.length]);

  const getEdgeId = useCallback((id) => {
    let res = node?.settings?.connections?.points?.filter((c) =>
      id?.includes(c?.id)
    );
    res = res?.map((res) => res?.edge?.slug);
    return res;
  }, [node?.settings?.connections?.points]);

  const onDeleteEdge = useCallback(async (id) => {
    const edgeId = getEdgeId(id);
    if (edgeId?.length > 0) {
      setRemovedEdges([...removedEdges, ...edgeId]);
    }
  }, [getEdgeId, removedEdges]);

  // Render Helpers
  const getNodeConfig = useCallback((nodeType) => {
    const prevNode = findPreviousNode(node);

    let hasFlowButton = false;
    if (prevNode?.data?.type === 'template') {
    const template = prevNode?.data?.template;
    const buttonsSection = template?.find(item => item.type === 'BUTTONS');
    const buttons = buttonsSection?.buttons || [];
    hasFlowButton = buttons.some(button => button.type === 'FLOW');
    } else {
    hasFlowButton = prevNode?.component_type === 'FLOW';
    }

    const flowItem = OPERATIONS_LIST.find(item => item.key === 'FLOW_COMPLETED');
    if (hasFlowButton) return flowItem;


    if (prevNode?.data?.subcategory_type === 'ORDER_DETAIL' || ['Payment Captured', 'Payment Failed'].includes(node?.label)) {
      const operationItem = OPERATIONS_LIST.find(item => item.label === node.label);
      if (operationItem) return operationItem;
    }

    const actionItem = ACTIONS_LIST.find(item => item.key === nodeType);
    if (actionItem) return actionItem;
    
    const operationItem = OPERATIONS_LIST.find(item => item.key === nodeType);
    if (operationItem) return operationItem;
    
    return {
      bg: '#04B8A2',
      headerBgColor: '#04B8A2',
      headerText: 'Send Message'
    };
  }, []);

  const getFilteredActionList = useCallback(() => {
    const prevNode = findPreviousNode(node);
    return ACTIONS_LIST.filter(action => {
      const isAfterApiTrigger = prevNode?.is_trigger && prevNode?.trigger_type === 'API_TRIGGER';
      return !isAfterApiTrigger || action?.key === 'TEMPLATE';
    });
  }, [findPreviousNode, node]);

  // Render Components
  const renderMessageTypesList = () => {
    const actionList = getFilteredActionList();
    
    return (
      <div className='min-w-full w-[488px] flex flex-col gap-1.5 p-2.5'>
        {actionList?.map((t) => {
          const isSelected = NODE_TYPES_CONVERSION[node?.type] === t?.label;
          return (
            <div
              key={t.key}
              className={cn(
                'w-full h-11 flex items-center justify-between gap-2.5 px-2.5 rounded-lg cursor-pointer',
                isSelected ? 'bg-[var(--BG-50)]' : 'hover:bg-[var(--BG-50)]'
              )}
              onClick={() => {
                onTriggerClick({ data: t, node: node, label: headerText });
                setIsHeaderTypeChange(false);
              }}>
              <div className='flex items-center gap-2.5'>
                <IconContainer color={t?.bg} width={30} height={30}>
                  <t.icon color={t?.color} width={18} height={18} />
                </IconContainer>
                <p>{t.label}</p>
              </div>
              {isSelected && (
                <img src={ICONS?.Selected} alt='selected' />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCTAUrlInputs = () => (
    <div className="flex gap-2 w-full">
      <div className='relative w-[30%]'>
        <Input
          label="Button Text"
          name="display_text"
          limit={20}
          value={node?.data?.interactive?.action?.parameters?.display_text || ''}
          onInputChange={(value) => {
            const updatedInteractive = {
              ...node.data?.interactive,
              action: {
                ...node.data?.interactive?.action,
                name: 'cta_url',
                parameters: {
                  ...node.data?.interactive?.action?.parameters,
                  display_text: value,
                },
              },
            };
            
            onChange({
              type: 'UPDATE_NODE',
              id: node?.uniq_id,
              primaryKey: 'data',
              key: 'interactive',
              data: updatedInteractive,
            });
          }}
          placeholder="e.g. Visit our website"
          showCount
          required
        />
        {!node?.data?.interactive?.action?.parameters?.display_text?.trim() && (
          <p className="text-xs text-red-500 mt-1">Button text is required</p>
        )}
      </div>
      <div className='w-[70%]'>
        <Input
          label="Destination URL"
          name="url"
          value={node?.data?.interactive?.action?.parameters?.url || ''}
          onInputChange={(value) => {
            const updatedInteractive = {
              ...node.data?.interactive,
              action: {
                ...node.data?.interactive?.action,
                name: 'cta_url',
                parameters: {
                  ...node.data?.interactive?.action?.parameters,
                  url: value,
                },
              },
            };
            
            onChange({
              type: 'UPDATE_NODE',
              id: node?.uniq_id,
              primaryKey: 'data',
              key: 'interactive',
              data: updatedInteractive,
            });
          }}
          placeholder="https://example.com"
          helperText="Include http:// or https://"
          limit={2000}
          required
        />
        {node?.data?.interactive?.action?.parameters?.url &&
          !validateURL(node.data.interactive.action.parameters.url) && (
            <p className="text-xs text-red-500 mt-1">Please enter a valid URL</p>
          )}
      </div>
    </div>
  );

  const renderQuickReplyButtons = () => (
    <div className="my-2.5 flex flex-col gap-2.5">
      {node?.data?.interactive?.action?.buttons?.map((b, i) => (
        <QuickReplyButtons
          key={b?.reply?.id || i}
          index={i + 1}
          onChange={onChange}
          showDelete={node?.data?.interactive?.action?.buttons?.length > 1}
          title={b?.reply?.title}
          limit={20}
          onInputChange={(value) =>
            allowToEditFlow &&
            onChange({
              type: 'UPDATE_INTERACTIVE_BUTTON',
              id: node?.uniq_id,
              key: i + 1,
              value,
            })
          }
          deleteButton={async () => {
            if (allowToEditFlow) {
              await onDeleteEdge([b?.reply?.id]);
              onChange({
                type: 'DELETE_INTERACTIVE_BUTTON',
                id: node?.id,
                key: i + 1,
              });
            }
          }}
        />
      ))}

      <button
        className={cn(
          'w-full h-8 text-sm text-[var(--font-600)] flex items-center justify-center border border-gray-200 border-dashed rounded-md',
          allowToEditFlow && allowToAddButton()
            ? 'opacity-100 cursor-pointer'
            : 'opacity-50 cursor-not-allowed'
        )}
        onClick={() =>
          allowToEditFlow &&
          allowToAddButton() &&
          onChange({ type: 'ADD_INTERACTIVE_BUTTON', id: node?.id })
        }
      >
        <AddIcon stroke="var(--font-600)" rect={false} width={22} height={22} />
        <p>Add button</p>
      </button>
    </div>
  );

  // Constants
  const nodeConfig = getNodeConfig(node?.type);
  const headerBgColor = nodeConfig?.headerBgColor || nodeConfig?.bg || '#04B8A2';
  const headerText = nodeConfig?.headerText || 'Send Message';
  const title = 'text-sm weight-semibold text-[var(--font-600)] pb-2.5';
  const actionTypes = ACTIONS_LIST.map(item => item?.key);
  const operationTypes = OPERATIONS_LIST.map(item => item?.key);

  return (
    <div className='w-full h-fit rounded-2xl overflow-hidden'>
      {/* Header */}
      <div 
        className='p-2.5 rounded-t-2xl rounded-b-md'
        style={{ backgroundColor: headerBgColor }}
      >
        <div className='flex items-center justify-between'>
          <p className='text-white'>{headerText}</p>
          <button
            className={cn(
              'size-4 flex items-center justify-center cursor-pointer',
              node?.status === 'UN_SAVED' || showSubmit()
                ? 'cursor-pointer'
                : 'cursor-not-allowed'
            )}
            onClick={handleCancelEditMode}>
            <Xmark
              color={
                node?.status === 'UN_SAVED' || showSubmit()
                  ? 'white'
                  : 'var(--BG-100)'
              }
              width={12}
              height={12}
            />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className='px-3.5 bg-white rounded-b-2xl max-h-[60vh] overflow-y-scroll listScroll'>
        {/* Message Type Selection */}
        {(actionTypes?.includes(node?.type) || ["PAYMENT_CAPTURED", "PAYMENT_FAILED", "FLOW_COMPLETED"].includes(node?.type)) && (
          <div className='py-5 border-b border-gray-200'>
            <h5 className={cn('', title)}>Message Type</h5>
            <Popover
              open={isHeaderTypeChange}
              content={renderMessageTypesList()}
              trigger={['click']}
              className='border border-gray-200 p-1 text-base rounded-md'
              arrow={false}
              onOpenChange={(open) => {
                if (allowToEditFlow) setIsHeaderTypeChange(open);
              }}>
              <button className='w-full flex items-center justify-between text-sm weight-semibold text-[#2D3036] py-2.5 px-4'>
                <p>{NODE_TYPES_CONVERSION[node?.type]}</p>
                <img
                  src={ICONS?.sidebarChevronDown}
                  alt=''
                  className={cn(
                    'transition-all ease-linear duration-200',
                    isHeaderTypeChange ? 'rotate-180' : ''
                  )}
                />
              </button>
            </Popover>
          </div>
        )}

        {/* Template Message */}
        {node?.data?.type === 'template' ? (
          <TemplateMessage
            node={node}
            onChange={onChange}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            allowToEditFlow={allowToEditFlow}
          />
        ) : (
          <div className='flex flex-col gap-5 py-5'>
            {/* Header Message */}
            {ALLOWED_HEADER_NODE_TYPES?.includes(node?.type) && (
              <HeaderMessage
                labelClassName={title}
                node={node}
                onChange={onChange}
                setError={setError}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                headerList={headerList}
                mediaRef={mediaRef}
              />
            )}

            {/* Body Message */}
            {ALLOWED_BODY_NODE_TYPES?.includes(node?.type) && (
              <BodyMessage
                labelClassName={title}
                value={
                  ['BUTTON', 'LIST', 'LOCATION_REQUEST_MESSAGE', 'FLOW', 'CTA_URL']?.includes(node?.type)
                    ? node?.data?.interactive?.body?.text || ''
                    : node?.data?.text?.body || ''
                }
                limit={1024}
                onChange={handleBodyChange}
              />
            )}

            {/* Footer Section */}
            {ALLOWED_FOOTER_NODE_TYPES?.includes(node?.type) && (
              <div className=''>
                <h5 className={cn('pb-2.5', title)}>Footer</h5>
                <Tabs
                  tabs={FOOTER_TYPES}
                  selected={footerTab}
                  onTabChange={handleFooterTabChange}
                />
              </div>
            )}

            {/* Footer Input */}
            {ALLOWED_FOOTER_NODE_TYPES?.includes(node?.type) &&
              footerTab?.key === 'TEXT' && (
                <Input
                  value={node?.data?.interactive?.footer?.text}
                  limit={60}
                  onInputChange={handleFooterChanges}
                />
              )}

            {/* Location Request Message */}
            {node?.type === 'LOCATION_REQUEST_MESSAGE' && (
              <div className='mt-4'>
                <h5 className={cn('pb-2.5', title)}>Location Request Button</h5>
                <div className='relative'>
                  <div className='relative w-full'>
                    <input
                      type='text'
                      disabled
                      value='Send Location'
                      className='w-full h-7 px-3 py-4 text-xs weight-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
                    />
                  </div>
                  <p className='mt-1 h-7 weight-medium text-xs text-gray-500'>
                    This button will request the user's location when clicked.
                  </p>
                </div>
              </div>
            )}

            {/* Contact Information */}
            {node?.type === 'CONTACTS' && (
              <div className='space-y-4'>
                <h5 className={cn('pb-2.5', title)}>Contact Information</h5>
                <ContactMessage node={node} onChange={onChange} />
              </div>
            )}

            {/* Follow Up Settings */}
            {node?.type === 'FOLLOW_UP' && (
              <div className=''>
                <FollowUps 
                  data={node?.data || {}} 
                  onChange={onChange}
                  node={node}
                />
              </div>
            )}

            {/* Assignee */}
            {node?.type === 'ASSIGNEE' && (
              <Assignee
                data={node.data}
                onChange={onChange}
                node={node}
              />
            )}

            {/* Button Configurations */}
            {ALLOWED_BUTTONS_NODE_TYPES?.includes(node?.type) && (
              <div>
                <h5 className={cn('pb-2.5', title)}>
                  {node?.type === 'LIST'
                    ? 'Button text'
                    : node?.type === 'FLOW'
                    ? 'Flow button'
                    : node?.type === 'CTA_URL'
                    ? 'Button text'
                    : 'Quick Reply Buttons'}
                </h5>

                {/* CTA URL Configuration */}
                {node?.type === 'CTA_URL' && renderCTAUrlInputs()}

                {/* Flow Configuration */}
                {node?.type === 'FLOW' && (
                  <FlowMessage
                    node={node}
                    onChange={onChange}
                    allowToEditFlow={allowToEditFlow}
                  />
                )}

                {/* List Button Configuration */}
                {node?.type === 'LIST' && (
                  <Input
                    value={node?.data?.interactive?.action?.button}
                    limit={60}
                    onInputChange={(value) =>
                      onChange({
                        type: 'UPDATE_LIST_NODE',
                        id: node?.id,
                        key: 'button',
                        value,
                      })
                    }
                  />
                )}

                {/* Quick Reply Buttons */}
                {node?.type === 'BUTTON' && renderQuickReplyButtons()}
              </div>
            )}
          </div>
        )}

        {/* List Message Configuration */}
        {node?.type === 'LIST' && (
          <div className='pb-4'>
            <ListMessage
              nodeId={node?.id}
              sections={node?.data?.interactive?.action?.sections}
              onChange={onChange}
              btnText={node?.data?.interactive?.action?.button}
              allowToEditFlow={allowToEditFlow}
              onDeleteEdge={onDeleteEdge}
            />
          </div>
        )}

        {/* Default Reply Configuration */}
        {actionTypes?.includes(node?.type) && (
          <div className='pb-4'>
            <DefaultReplay
              data={node?.settings?.other_replies}
              onChange={onChange}
              nodeId={node?.id}
            />
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div
        className='w-full h-12 flex items-center justify-end px-5'
        style={{
          boxShadow: '-1px -3px 31px -11px rgba(0,0,0,0.75)',
          WebkitBoxShadow: '1px -3px 31px -11pxx rgba(0,0,0,0.75)',
          MozBoxShadow: '1px -3px 31px -11px rgba(0,0,0,0.75)',
        }}>
        <button
          className={cn(
            'w-14 h-8 text-sm weight-semibold bg-[var(--new-primary)] text-white flex items-center justify-center border border-[var(--primary)] rounded-md',
            showSubmit()
              ? 'cursor-pointer opacity-100'
              : 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => showSubmit() && handleOk()}
          disabled={!showSubmit() || isUpdating}>
          {isUpdating ? (
            <Loader
              Width={14}
              Height={14}
              loaderBg='white'
            />
          ) : (
            <div className='flex items-center justify-center gap-1.5'>
              <Tick
                width='12'
                height='10'
                strokeWidth='2.5'
                stroke='white'
              />
              <span>OK</span>
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default EditMessages;
